import { S3FileUploader } from "@contember/client";
import { useRef, useReducer, useMemo, useCallback, useEffect } from "react";
import { fileUploadReducer, initializeFileUploadState } from "./fileUploadReducer.js";
import { useCurrentContentGraphQlClient } from "../content/useCurrentContentGraphQlClient.js";
const useFileUpload = ({} = {}) => {
  const contentApiClient = useCurrentContentGraphQlClient();
  const fileIdSeedRef = useRef(1);
  const [internalState, dispatch] = useReducer(fileUploadReducer, void 0, initializeFileUploadState);
  const defaultUploader = useMemo(() => new S3FileUploader(), []);
  const purgeUpload = useCallback((files) => {
    dispatch({
      type: "purge",
      files
    });
  }, []);
  const failUpload = useCallback((error) => {
    dispatch({
      type: "finishWithError",
      error
    });
  }, []);
  const previewUrls = useRef(/* @__PURE__ */ new Set());
  const fileIds = useRef(/* @__PURE__ */ new WeakMap());
  const filesWithMetadataById = useRef(/* @__PURE__ */ new Map());
  const initializeUpload = useCallback(
    (files) => {
      const newFileIds = /* @__PURE__ */ new Set();
      const fileWithMetadataByFileConfig = /* @__PURE__ */ new Map();
      for (const fileMaybeWithId of files) {
        const abortController = new AbortController();
        let fileId;
        let file;
        if (fileMaybeWithId instanceof File) {
          fileId = `__contember__file-${fileIdSeedRef.current++}`;
          file = fileMaybeWithId;
        } else {
          fileId = fileMaybeWithId[0];
          file = fileMaybeWithId[1];
        }
        abortController.signal.addEventListener("abort", () => {
          purgeUpload([fileId]);
        });
        const previewUrl = URL.createObjectURL(file);
        const fileWithMetadata = {
          previewUrl,
          abortController,
          file,
          fileId
        };
        fileWithMetadataByFileConfig.set(fileId, fileWithMetadata);
        newFileIds.add(fileId);
        previewUrls.current.add(previewUrl);
        fileIds.current.set(file, fileId);
        filesWithMetadataById.current.set(fileId, new WeakRef(fileWithMetadata));
      }
      if (fileWithMetadataByFileConfig.size === 0) {
        return fileWithMetadataByFileConfig;
      }
      if (newFileIds.size) {
        dispatch({
          type: "purge",
          files: newFileIds
        });
      }
      dispatch({
        type: "initialize",
        files: fileWithMetadataByFileConfig
      });
      return fileWithMetadataByFileConfig;
    },
    [purgeUpload]
  );
  const startUpload = useCallback(
    (files) => {
      const filesById = /* @__PURE__ */ new Map();
      const groupedByUploader = /* @__PURE__ */ new Map();
      for (const fileOrIdMaybeWithOptions of files) {
        let fileOrId;
        let options;
        if (Array.isArray(fileOrIdMaybeWithOptions)) {
          fileOrId = fileOrIdMaybeWithOptions[0];
          options = fileOrIdMaybeWithOptions[1];
        } else {
          fileOrId = fileOrIdMaybeWithOptions;
          options = {};
        }
        const { uploader = defaultUploader, metadata } = options;
        const fileId = fileOrId instanceof File ? fileIds.current.get(fileOrId) : fileOrId;
        const fileState = fileId ? filesWithMetadataById.current.get(fileId)?.deref() : void 0;
        if (fileId === void 0 || fileState === void 0) {
          throw new Error(
            `Trying to startUpload a file that hasn't been previously initialized. This will be possible in future, but isn't yet.`
          );
        }
        filesById.set(fileId, {
          uploader,
          metadata
        });
        let filesWithSameUploader = groupedByUploader.get(uploader);
        if (filesWithSameUploader === void 0) {
          groupedByUploader.set(uploader, filesWithSameUploader = /* @__PURE__ */ new Map());
        }
        filesWithSameUploader.set(fileState.file, {
          abortSignal: fileState.abortController.signal
        });
      }
      dispatch({
        type: "startUploading",
        files: filesById
      });
      for (const [uploader, filesForUpload] of groupedByUploader) {
        try {
          uploader.upload(filesForUpload, {
            onProgress: (progress) => {
              dispatch({
                type: "updateUploadProgress",
                progress
              });
            },
            onSuccess: (result) => {
              dispatch({
                type: "finishSuccessfully",
                result
              });
            },
            onError: (error) => {
              dispatch({
                type: "finishWithError",
                error
              });
            },
            contentApiClient
          });
        } catch (error) {
          dispatch({
            type: "finishWithError",
            error: Array.from(filesForUpload).map(([file]) => [file, error])
          });
        }
      }
    },
    [contentApiClient, defaultUploader]
  );
  const operations = useMemo(
    () => ({
      failUpload,
      initializeUpload,
      purgeUpload,
      startUpload
    }),
    [failUpload, initializeUpload, purgeUpload, startUpload]
  );
  useEffect(
    () => () => {
      for (const previewUrl of Array.from(previewUrls.current)) {
        URL.revokeObjectURL(previewUrl);
      }
    },
    []
  );
  return [internalState.state, operations];
};
export {
  useFileUpload
};
//# sourceMappingURL=useFileUpload.js.map
